import React, { useContext, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { triggerNotification } from '../../../helpers/notification';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import PrintDesignEditor from '../designer/PrintDesignEditor';

const ExternalProductDesignView = ({ config }) => {
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const { search } = useLocation();
  const productIds = new URLSearchParams(search).get('ids')?.split(',') ?? [];
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchExternalProductsImportData,
    fetchVendorProductPrintDesignOptions,
    createVendorProductsFromPrintTemplates,
    createVendorDesigns
  } = useContext(VendorContext);

  const { value: externalProducts, loading } = state.externalProductsImportData;

  useEffect(() => {
    if (config && authState.pkEcomVendor) {
      fetchExternalProductsImportData(
        config.pkEcomExternalConnectionType,
        {
          fkEcomExternalConnectionType: config.pkEcomExternalConnectionType,
          externalProductIds: productIds
        },
        null,
        (error) => {
          triggerNotification(error);
        }
      );

      fetchVendorProductPrintDesignOptions(authState.pkEcomVendor);
      hasFetched.current = true;
    }
  }, [config?.pkEcomExternalConnectionType, authState.pkEcomVendor]);

  return (
    <PrintDesignEditor
      ecomExternalProducts={externalProducts.map((p) => ({
        id: p.productExternalId,
        ...p.ecomVendorProduct
      }))}
      fkEcomVendor={authState.pkEcomVendor}
      loading={!hasFetched.current || loading}
      onCreateDesigns={({ designs }, onSuccessCallback, onErrorCallback) => {
        createVendorDesigns(
          authState.pkEcomVendor,
          {
            ecomVendorDesigns: designs.map((d) => ({
              ...d,
              fkEcomVendorMedia: d.pkMedia
            }))
          },
          (data) => {
            onSuccessCallback(
              data.map((d) => ({
                pkDesign: d.pkEcomVendorDesign,
                pkMedia: d.fkEcomVendorMedia,
                name: d.name
              }))
            );
          },
          onErrorCallback
        );
      }}
      onSaveChanges={(
        { productDesigns },
        onSuccessCallback,
        onErrorCallback
      ) => {
        createVendorProductsFromPrintTemplates(
          authState.pkEcomVendor,
          {
            productDesigns: productDesigns.map((d) => ({
              ...d,
              fkEcomVendorDesign: d.pkDesign
            }))
          },
          (data) => {
            const printDesign = data[0];
            onSuccessCallback({
              pkPrintDesign: printDesign.pkEcomVendorProductPrintDesign,
              pkProduct: printDesign.pkEcomVendorProduct,
              productName: printDesign.name,
              fkEcomExternalConnectionType:
                printDesign.ecomVendorProductExternalIds[0]
                  ?.fkEcomExternalConnectionType,
              printDesignPreview: printDesign?.printImageUrl,
              printImageUrl: printDesign?.printImageUrl,
              canvasState: {
                items: printDesign.ecomVendorProductPrintDesignItems.map(
                  (i) => ({
                    ...i,
                    id: i.pkEcomVendorProductPrintDesignItem.toString(),
                    pkDesignItem: i.pkEcomVendorProductPrintDesignItem,
                    visible: !i.hidden
                  })
                )
              }
            });
          },
          onErrorCallback
        );
      }}
      onSaveChangesComplete={() => {
        triggerNotification('Products created!', 'success');
        navigate('/vendor/products');
      }}
      options={{
        designs:
          state.ecomVendorProductPrintDesignOptions.value?.ecomVendorDesigns.map(
            (d) => ({
              pkDesign: d.pkEcomVendorDesign,
              name: d.name,
              pkMedia: d.fkEcomVendorMedia
            })
          ) || []
      }}
      productIds={productIds}
    />
  );
};

ExternalProductDesignView.propTypes = { config: PropTypes.object.isRequired };

export default ExternalProductDesignView;
